import { useTranslation } from 'react-i18next';

import { T, SERIF, SANS, MONO } from '@/ui/brandTokens';

// Local aliases over the canonical tokens (this page predates the
// shared token module; aliases keep the JSX below unchanged).
const INK = T.ink;
const BG = T.bg;
const BORDER = T.border;
const MUTED = T.muted;
const FAINT = T.faint;
const WHITE = T.white;
const WORDMARK = "'Barlow Condensed', 'Trade Gothic Next LT Pro Cn', sans-serif";

const SUPPORT_EMAIL = 'partners@altailabs.io';

/**
 * Fallback surface for unhandled errors — rendered both by
 * `ErrorBoundary` (render-time exceptions) and directly for 5xx
 * integration tests. The request id is read from
 * `window.__lastRequestId` (stamped by apiFetch on the most recent
 * backend call) and quoted in the mailto body so support can tie
 * the email back to a trace.
 */
function ServerErrorPage() {
  const { t } = useTranslation('common');
  const requestId =
    typeof window !== 'undefined'
      ? ((window as unknown as { __lastRequestId?: string }).__lastRequestId ?? null)
      : null;

  const subject = encodeURIComponent(t('errorPages.server.mailSubject'));
  const body = encodeURIComponent(
    [
      t('errorPages.server.mailIntro'),
      '',
      requestId ? `${t('errorPages.server.requestIdLabel')}: ${requestId}` : null,
      `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
    ]
      .filter(Boolean)
      .join('\n'),
  );
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;

  return (
    <div
      style={{
        minHeight: 'var(--app-height, 100vh)',
        background: BG,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        padding: '48px 24px',
        textAlign: 'center',
      }}
    >
      <span
        style={{
          fontFamily: WORDMARK,
          fontSize: 24,
          fontWeight: 500,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          color: INK,
        }}
      >
        <span style={{ fontStyle: 'italic', marginRight: '0.02em' }}>A</span>LTAILABS
      </span>
      <div
        style={{
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: FAINT,
        }}
      >
        {t('errorPages.server.kicker')}
      </div>
      <h1
        style={{
          fontFamily: SERIF,
          fontSize: 36,
          color: INK,
          letterSpacing: '-0.02em',
          margin: 0,
          maxWidth: 560,
        }}
      >
        {t('errorPages.server.title')}
      </h1>
      <p
        style={{
          fontFamily: SANS,
          fontSize: 15,
          color: MUTED,
          lineHeight: 1.7,
          maxWidth: 520,
          margin: 0,
        }}
      >
        {t('errorPages.server.body')}
      </p>
      {requestId && (
        <div
          style={{
            fontFamily: MONO,
            fontSize: 12,
            color: FAINT,
            padding: '10px 14px',
            border: `1px solid ${BORDER}`,
            borderRadius: 8,
            background: WHITE,
          }}
        >
          {t('errorPages.server.requestIdLabel')}: {requestId}
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
        <a
          href={mailto}
          style={{
            height: 48,
            padding: '0 24px',
            background: INK,
            color: WHITE,
            borderRadius: 8,
            fontFamily: SANS,
            fontSize: 14,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            textDecoration: 'none',
          }}
        >
          {t('errorPages.server.contactSupport')}
        </a>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            height: 48,
            padding: '0 24px',
            background: WHITE,
            color: INK,
            border: `1px solid ${BORDER}`,
            borderRadius: 8,
            fontFamily: SANS,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t('errorPages.server.retry')}
        </button>
      </div>
    </div>
  );
}

export default ServerErrorPage;
